import React, { useState, useEffect } from 'react';
import axios from 'axios';
import UploadImage from '../../components/UploadImage';
import '../../styles/SetOffer.css';
import backendUrl from '../../BackendUrl';

const SetOffer = () => {
  // State for the list of offers
  const [offers, setOffers] = useState([]);

  // State to control whether the add/edit modal (canvas) is visible
  const [isCanvasVisible, setIsCanvasVisible] = useState(false);
  // State to decide if we’re editing (true) or adding (false)
  const [isEditMode, setIsEditMode] = useState(false);
  // The offer selected for editing (or null when adding)
  const [selectedOffer, setSelectedOffer] = useState(null);
  const adminToken = localStorage.getItem('adminToken');

  // Form data for offer information
  const [formData, setFormData] = useState({
    offer: '',
    description: '',
    percent: '',
    expires: '',
  });

  // Fetch the offers when the component mounts
  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/offers/get-offer`);
      setOffers(res.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  // Opens the modal for adding a new offer
  const handleAddClick = () => {
    setIsEditMode(false);
    setSelectedOffer(null);
    setFormData({
      offer: '',
      description: '',
      percent: '',
      expires: '',
    });
    setIsCanvasVisible(true);
  };

  // Opens the modal for editing an existing offer and pre-fills the form
  const handleEditClick = (offer) => {
    setIsEditMode(true);
    setSelectedOffer(offer);
    setFormData({
      offer: offer.offer,
      description: offer.description,
      percent: offer.percent,
      // Format the date for the input (assumes ISO string)
      expires: offer.expires ? offer.expires.split('T')[0] : '',
    });
    setIsCanvasVisible(true);
  };

  // Closes the modal
  const handleCancel = () => {
    setIsCanvasVisible(false);
  };

  // Handle changes in the form inputs
  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle form submission for both adding and editing
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditMode && selectedOffer) {
        // Update an existing offer
        await axios.put(`${backendUrl}/api/offers/update-offer/${selectedOffer._id}`, formData, {headers: {Authorization: `Bearer ${adminToken}`,},});
      } else {
        // Create a new offer
        await axios.post(`${backendUrl}/api/offers/add-offer`, formData, {headers: {Authorization: `Bearer ${adminToken}`,},});
      }
      // Refresh the list and close the modal
      fetchOffers();
      setIsCanvasVisible(false);
    } catch (error) {
      console.error('Error saving offer:', error);
    }
  };

  // Delete an offer
  const handleDelete = async (offerId) => {
    if (window.confirm('Are you sure you want to delete this offer?')) {
      try {
        await axios.delete(`${backendUrl}/api/offers/delete-offer/${offerId}`, {headers: {Authorization: `Bearer ${adminToken}`,},});
        fetchOffers();
      } catch (error) {
        console.error('Error deleting offer:', error);
      }
    }
  };

  // Handle image upload success from the UploadImage component.
  // Here we simply re-fetch the offers to update the image if needed.
  const handleUploadSuccess = (data) => {
    console.log('Image uploaded successfully:', data);
    fetchOffers();
  };

  return (
    <div className="admin-offers-page">
      <h1>Manage Offers</h1>
      <button onClick={handleAddClick} className="add-offer-btn">Add Offer</button>

      <div className="offers-list">
        {offers.map((offer) => (
          <div key={offer._id} className="offer-card">
            {/* Show the offer image (or default image if none) */}
            <img src={offer.offerImg} alt={offer.offer} className="offer-image" />
            <div className="offer-details">
              <h3>{offer.offer}</h3>
              <p>{offer.description}</p>
              <p>Discount: {offer.percent}%</p>
              <p>Expires: {offer.expires ? new Date(offer.expires).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div className="offer-actions">
              <button onClick={() => handleEditClick(offer)}>Edit</button>
              <button onClick={() => handleDelete(offer._id)}>Delete</button>
            </div>
            {/* UploadImage component to allow image uploads */}
            <UploadImage 
              uploadUrl="/api/offers/upload-offer" 
              id={offer._id}
              onUploadSuccess={handleUploadSuccess}
            />
          </div>
        ))}
      </div>

      {/* Modal canvas for add/edit form */}
      {isCanvasVisible && (
        <div className="offer-canvas-overlay">
          <div className="offer-canvas">
            <button className="close-button" onClick={handleCancel}>X</button>
            <h2>{isEditMode ? 'Edit Offer' : 'Add Offer'}</h2>
            <form onSubmit={handleFormSubmit} className="offer-form">
              <div className="form-group">
                <label>Offer:</label>
                <input 
                  type="text" 
                  name="offer" 
                  value={formData.offer} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Percent:</label>
                <input 
                  type="number" 
                  name="percent" 
                  value={formData.percent} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Expires:</label>
                <input 
                  type="date" 
                  name="expires" 
                  value={formData.expires} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="form-buttons">
                <button type="submit">{isEditMode ? 'Update Offer' : 'Add Offer'}</button>
                <button type="button" onClick={handleCancel}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetOffer;